import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { SubscriptionPauseApiService } from './subscription-pause-api.service';
import {
  PauseDates,
  PauseErrorCode,
  PauseOutcome,
  PauseSubject,
} from '../shared/types/subscription-pause.types';
import { toDate } from '../shared/utils/subscription-pause.util';
import {
  PauseFlowActions,
  PauseFlowController,
  PauseFlowOptions,
} from '../shared/utils/pause-flow.controller';

const KNOWN_CODES = new Set<PauseErrorCode>([
  'PAUSE_DISABLED',
  'FAMILY_PLAN',
  'NOT_ELIGIBLE',
  'NOT_ACTIVE',
  'CANCEL_PENDING',
  'ALREADY_PAUSED',
  'BILLING_RESTART_PENDING',
  'PLAN_CHANGE_PENDING',
  'INVALID_DATES',
  'START_IN_PAST',
  'TOO_SHORT',
  'TOO_LONG',
  'SUBSCRIPTION_NOT_FOUND',
  'PAUSE_UNSUPPORTED_SCHEDULE',
  'NO_PAYMENT_METHOD',
  'BILLING_UPDATE_FAILED',
  'NOT_SCHEDULED',
  'NOT_PAUSED',
]);

/** Reads `data.code` from an API refusal; 403s without one are role or tenant refusals. */
export function toPauseErrorCode(error: unknown): PauseErrorCode {
  const http = error instanceof HttpErrorResponse ? error : null;
  const code = http?.error?.data?.code;
  if (typeof code === 'string' && KNOWN_CODES.has(code as PauseErrorCode)) {
    return code as PauseErrorCode;
  }
  return http?.status === 403 ? 'FORBIDDEN' : 'UNKNOWN';
}

/**
 * Membership pause actions (CWS-1469): calls the API and reports what happened as a
 * PauseOutcome, or fails with a PauseErrorCode. No display text; see subscription-pause.copy.ts.
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionPauseActionsService implements PauseFlowActions {
  constructor(private readonly api: SubscriptionPauseApiService) {}

  pause(subject: PauseSubject, dates: PauseDates): Observable<PauseOutcome> {
    return this.api.pause(subject.subscriptionId, dates).pipe(
      map(pause => {
        const start = toDate(pause?.start) ?? toDate(dates.start) ?? new Date();
        const resumeDate = toDate(pause?.resume_date) ?? toDate(dates.resume_date) ?? start;
        const kind = pause?.status === 'scheduled' ? 'scheduled' : 'paused';
        return { kind, plate: subject.plate, start, resumeDate, pause } as PauseOutcome;
      }),
      catchError(error => throwError(() => toPauseErrorCode(error)))
    );
  }

  activate(subject: PauseSubject): Observable<PauseOutcome> {
    return this.api.resume(subject.subscriptionId).pipe(
      map((pause): PauseOutcome => ({ kind: 'activated', plate: subject.plate, pause })),
      catchError(error => throwError(() => toPauseErrorCode(error)))
    );
  }

  cancelScheduled(subject: PauseSubject): Observable<PauseOutcome> {
    return this.api.cancelScheduled(subject.subscriptionId).pipe(
      map((pause): PauseOutcome => ({ kind: 'scheduled_cancelled', plate: subject.plate, pause })),
      catchError(error => throwError(() => toPauseErrorCode(error)))
    );
  }

  /** A pause flow for one host; see PauseFlowController. */
  createFlow(options: PauseFlowOptions = {}): PauseFlowController {
    return new PauseFlowController(this, options);
  }
}
